import React, { useState, useEffect } from 'react'
import type { QuizAttemptWithStudent, EssayGradeItem } from '@/types/quiz'
import { useQuizAttemptDetail, useQuestions, useGradeQuizAttempt } from '@/hooks/useQuizzes'

interface QuizAttemptReviewModalProps {
  attempt: QuizAttemptWithStudent
  onClose: () => void
  onGraded: () => void
}

export const QuizAttemptReviewModal: React.FC<QuizAttemptReviewModalProps> = ({
  attempt,
  onClose,
  onGraded,
}) => {
  const { data: attemptDetail, isLoading: detailLoading } = useQuizAttemptDetail(attempt.id)
  const { data: questions, isLoading: questionsLoading } = useQuestions(attempt.quizId)
  const gradeQuizAttempt = useGradeQuizAttempt()

  const [essayGrades, setEssayGrades] = useState<Record<string, { score: string; feedback: string }>>({})
  const [overallFeedback, setOverallFeedback] = useState(attempt.feedback || '')
  const [error, setError] = useState('')

  // Initialize essay grades from existing data
  useEffect(() => {
    if (questions) {
      const existingGrades: Record<string, { score: string; feedback: string }> = {}

      // Parse existing grades if already graded
      let parsedGrades: Record<string, { score: number; feedback: string }> = {}
      if (attemptDetail?.essayGrades) {
        parsedGrades = typeof attemptDetail.essayGrades === 'string'
          ? JSON.parse(attemptDetail.essayGrades)
          : attemptDetail.essayGrades || {}
      }

      questions.forEach((q) => {
        if (q.questionType === 'essay') {
          const existing = parsedGrades[q.id]
          existingGrades[q.id] = {
            score: existing ? String(existing.score) : '',
            feedback: existing?.feedback || '',
          }
        }
      })
      setEssayGrades(existingGrades)
    }
  }, [attemptDetail, questions])

  const handleGradeChange = (questionId: string, field: 'score' | 'feedback', value: string) => {
    setEssayGrades((prev) => ({
      ...prev,
      [questionId]: { ...prev[questionId], [field]: value },
    }))
  }

  const handleSubmit = async () => {
    setError('')

    // Validate all essay questions are graded
    const grades: EssayGradeItem[] = []
    for (const [questionId, grade] of Object.entries(essayGrades)) {
      const scoreNum = parseInt(grade.score)
      if (isNaN(scoreNum) || scoreNum < 0) {
        setError(`Please enter a valid score for question ${questionId}`)
        return
      }
      const question = questions?.find(q => q.id === questionId)
      if (question && scoreNum > question.points) {
        setError(`Score cannot exceed ${question.points} points for question ${questionId}`)
        return
      }
      grades.push({
        questionId,
        score: scoreNum,
        feedback: grade.feedback,
      })
    }

    if (grades.length === 0) {
      setError('No essay questions to grade')
      return
    }

    try {
      await gradeQuizAttempt.mutateAsync({
        attemptId: attempt.id,
        data: {
          essayGrades: grades,
          feedback: overallFeedback,
        },
      })
      onGraded()
    } catch (err) {
      console.error('Failed to grade quiz attempt:', err)
      alert('Failed to save grades. Please try again.')
    }
  }

  const getEssayAnswer = (questionId: string): string => {
    if (!attemptDetail?.essayAnswers) return ''
    const answers = typeof attemptDetail.essayAnswers === 'string'
      ? JSON.parse(attemptDetail.essayAnswers)
      : attemptDetail.essayAnswers
    return answers[questionId] || answers[questionId?.replace(/-/g, '_')] || ''
  }

  const isLoading = detailLoading || questionsLoading
  const isGrading = gradeQuizAttempt.isPending

  // Parse MC answers
  const mcAnswers = attemptDetail?.answers
    ? (typeof attemptDetail.answers === 'string'
        ? JSON.parse(attemptDetail.answers)
        : attemptDetail.answers || {})
    : {}

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Review: {attempt.studentName}'s Attempt #{attempt.attemptNumber}
            </h3>
            <p className="text-sm text-gray-500">{attempt.studentEmail}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{attempt.score}%</div>
                  <div className="text-sm text-gray-500">Score</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className={`text-2xl font-bold ${attempt.status === 'graded' ? 'text-green-600' : 'text-amber-600'}`}>
                    {attempt.status === 'graded' ? 'Graded' : 'Pending'}
                  </div>
                  <div className="text-sm text-gray-500">Status</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <div className={`text-2xl font-bold ${attempt.isPassed ? 'text-green-600' : 'text-red-600'}`}>
                    {attempt.isPassed ? 'Passed' : 'Failed'}
                  </div>
                  <div className="text-sm text-gray-500">Result</div>
                </div>
              </div>

              {/* Questions */}
              {questions?.map((question, idx) => {
                if (question.questionType === 'essay') {
                  // Essay question
                  const essayAnswer = getEssayAnswer(question.id)
                  const grade = essayGrades[question.id]

                  return (
                    <div key={question.id} className="mb-6 border border-amber-200 rounded-lg p-4 bg-amber-50">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <span className="inline-block px-2 py-1 text-xs font-semibold text-amber-700 bg-amber-200 rounded mr-2">
                            Essay
                          </span>
                          <span className="text-sm text-gray-500">Question {idx + 1}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-700">{question.points} pts</span>
                      </div>
                      <p className="font-medium text-gray-900 mb-3">{question.questionText}</p>

                      {/* Student Answer */}
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-600 mb-1">Student Answer:</label>
                        <div className="bg-white border border-gray-200 rounded-lg p-3 text-gray-800 whitespace-pre-wrap min-h-[80px]">
                          {essayAnswer || <span className="text-gray-400 italic">No answer provided</span>}
                        </div>
                      </div>

                      {/* Grade Input */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Score <span className="text-red-500">*</span>
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              value={grade?.score || ''}
                              onChange={(e) => handleGradeChange(question.id, 'score', e.target.value)}
                              min="0"
                              max={question.points}
                              className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0"
                              disabled={isGrading}
                            />
                            <span className="text-gray-600">/ {question.points}</span>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Feedback</label>
                          <input
                            type="text"
                            value={grade?.feedback || ''}
                            onChange={(e) => handleGradeChange(question.id, 'feedback', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Optional feedback..."
                            disabled={isGrading}
                          />
                        </div>
                      </div>
                    </div>
                  )
                }

                // MC question
                const mcAnswer = mcAnswers[question.id] ?? mcAnswers[question.id?.replace(/-/g, '_')]
                const isCorrect = mcAnswer === question.correctAnswer

                return (
                  <div key={question.id} className="mb-4 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <span className={`inline-block px-2 py-1 text-xs font-semibold rounded mr-2 ${
                          isCorrect ? 'text-green-700 bg-green-200' : 'text-red-700 bg-red-200'
                        }`}>
                          {isCorrect ? 'Correct' : 'Incorrect'}
                        </span>
                        <span className="text-sm text-gray-500">Question {idx + 1}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{question.points} pts</span>
                    </div>
                    <p className="font-medium text-gray-900 mb-2">{question.questionText}</p>
                    <div className="text-sm text-gray-600">
                      Selected: {question.options[mcAnswer] || 'N/A'} | Correct: {question.options[question.correctAnswer]}
                    </div>
                  </div>
                )
              })}

              {/* Overall Feedback */}
              <div className="mt-6 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Overall Feedback</label>
                <textarea
                  value={overallFeedback}
                  onChange={(e) => setOverallFeedback(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  placeholder="Berikan feedback keseluruhan untuk peserta..."
                  disabled={isGrading}
                />
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isGrading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isLoading || isGrading}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {isGrading ? (
              <>
                <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              'Save Grades'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
